import * as React from 'react';
import * as PropTypes from 'prop-types';
import { clickHandler } from '../../../utils/util';
import { bindActionCreators, Dispatch } from 'redux';
import * as LoginActions from '../../../store/pages/login/actions';
import { connect } from 'react-redux';
import { Product } from '../../../store/pages/products/types';
import * as ct from '../../global/controls';
import * as v from '../../global/validation';
import { ValidationError } from '../../../store/global/types';
import { ApplicationState } from '../../../store';
import * as api from '../../../store/apiClient';
import Loading from '../../global/loading';
import ApiError from '../../global/apiError';
import { hasStockConfiguration } from "../../../store/pages/products/helpers";

interface MappedReduxState {
    selectedVenueId: string;
    validationErrors: ValidationError[];
    products: Product[];
    productsLoading: boolean;
}

interface Actions {
    logout: () => void;
}

interface LocalProps {
    close: () => void;
}

type StockProductManualRestockProps = MappedReduxState & Actions & LocalProps;

interface StockProductManualRestockState {
    validationError: string | null;
    selectedProductId: ct.FormValue<string>,
    selectedProductVariantId: ct.FormValue<string | null>;
    quantity: ct.FormValue<number | null>;
    isSaving: boolean;
    savingError: api.ApiError | null;
}

class StockProductManualRestock extends React.Component<StockProductManualRestockProps, StockProductManualRestockState> {
    constructor(props: StockProductManualRestockProps) {
        super(props);
        this.state = this.buildStateFromProps(props);
    }

    static contextTypes = {
        t: PropTypes.func
    }

    private buildStateFromProps(props: StockProductManualRestockProps): StockProductManualRestockState {
        const prod = this.validateProductId('');
        return {
            validationError: null,
            selectedProductId: prod,
            selectedProductVariantId: this.validateProductVariantId('', prod.value),
            quantity: this.validateQuantity(null),
            isSaving: false,
            savingError: null,
        };
    }

    isProductVariantRequired = (val: string | null, productId: string, msg: string) => {
        const hasVariants = this.doesProductHaveVariants(productId);
        return (hasVariants && !val) ? msg : undefined;
    }

    validateQuantity = (val: number | null) => v.validate(val, 'quantity', [v.required, v.numeric], this.props.validationErrors);
    validateProductId = (val: string) => v.validate(val, 'selectedProductId', [v.required], []);

    validateProductVariantId = (val: string | null, productId: string) => v.validate(val, `selectedProductVariantId`, [v => this.isProductVariantRequired(val, productId, 'StockProductManualRestock:selectedProductVariantIdIsMandatoryMsg')], this.props.validationErrors);

    doesProductHaveVariants = (productId: string) => {
        const { products } = this.props;
        const product = products.find(p => p.id === productId);
        return (product?.variants ?? []).length > 0;
    }

    selectedProductIdChanged = (productId: string) => {
        const validatedProductId = this.validateProductId(productId);
        this.setState(s => ({ selectedProductId: validatedProductId, selectedProductVariantId: this.validateProductVariantId('', validatedProductId.value) }));
    }

    selectedProductVariantIdChanged = (val: string) => {
        this.setState(s => ({ selectedProductVariantId: this.validateProductVariantId(val, s.selectedProductId.value) }));
    }

    private onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
    }

    private saveManualRestock = (quantity: number) => {
        const { selectedVenueId, close, logout } = this.props;
        const { selectedProductId, selectedProductVariantId } = this.state;
        const body = {
            venueId: selectedVenueId,
            productId: selectedProductId.value,
            productVariantId: selectedProductVariantId.value,
            quantity: quantity
        }

        api.postWithAuth(`api/v1/productStock/restock`, body, logout)
            .subscribe(response => {
                    this.setState({ isSaving: false, savingError: null })
                    close();
                },
                (err: api.ApiError) => { this.setState({ isSaving: false, savingError: err }) });
    }

    private save = () => {
        if (!v.isValid(this.state)) {
            this.setState({ validationError: this.context.t('Global:formNotValid') });
            // TODO: Show error message!
        } else {
            this.setState({ validationError: null, isSaving: true });
            const { quantity } = this.state;
            const restockAmount = quantity.value ?? 0;
            this.saveManualRestock(restockAmount);
        }
    }

    private shouldShowVariantField = (): boolean => {
        const { selectedProductId } = this.state;

        if (!selectedProductId.value) {
            return false;
        }

        return this.doesProductHaveVariants(selectedProductId.value);
    };

    private renderProductSelect = () => {
        const { t } = this.context;
        const { products, selectedVenueId } = this.props;
        const { selectedProductId } = this.state;

        const productOptions = [
            { key: '', name: t('StockProductManualRestock:selectProduct') },
            ...products
                .filter(hasStockConfiguration)
                .filter(p => p.venueId === selectedVenueId && !p.isActivityProduct)
                .map(p => ({ key: p.id, name: p.name }))
        ];

        return (
            <div>
                <ct.Select
                    id='selectedProductId'
                    labelKey='StockProductManualRestock:selectedProductId'
                    value={{ ...selectedProductId, value: selectedProductId.value || '' }}
                    callback={this.selectedProductIdChanged}
                    options={productOptions}
                />
            </div>
        );
    };

    private renderProductVariantSelect = () => {
        if (!this.shouldShowVariantField()) return null;

        const { t } = this.context;
        const { products } = this.props;
        const { selectedProductId, selectedProductVariantId } = this.state;

        const productVariantOptions = [
            { key: '', name: t('StockProductManualRestock:selectProductVariant') },
            ...(products.find(p => p.id === selectedProductId.value)?.variants ?? [])
                .map(v => ({ key: v.id, name: v.name }))
        ];

        return (
            <div>
                <ct.Select
                    id='selectedProductVariantId'
                    labelKey='StockProductManualRestock:selectedProductVariantId'
                    value={{ ...selectedProductVariantId, value: selectedProductVariantId.value || '' }}
                    callback={this.selectedProductVariantIdChanged}
                    options={productVariantOptions}
                />
            </div>
        );
    };

    render() {
        const { t } = this.context;
        const { close, productsLoading } = this.props;
        const { quantity, isSaving, savingError } = this.state;

        if (isSaving || productsLoading) return <Loading />;
        if (savingError) return <ApiError error={ savingError } />;

        const hasValueChanged = quantity.value !== 0;

        return <div>
            <h2>{t('StockProductManualRestock:heading')}</h2>
            <form className='data-form' onSubmit={this.onFormSubmit} autoComplete='off'>
                {this.renderProductSelect()}
                {this.renderProductVariantSelect()}
                <ct.NumberBox id='quantity' labelKey='StockProductManualRestock:addQuantity' value={quantity} callback={val => this.setState(s => ({ quantity: this.validateQuantity(val || 0) }))} min='1' />
                <div className='btn-toolbar' style={({ marginTop: '20px' })}>
                    <button className='btn btn-primary' disabled={!hasValueChanged} onClick={e => clickHandler(e, this.save)}>{t('Global:save')}</button>
                    <button className='btn btn-basic' onClick={e => clickHandler(e, close)}>{t('Global:cancel')}</button>
                </div>
            </form>
        </div>
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    selectedVenueId: state.venues.selectedVenueId,
    validationErrors: state.products.validationErrors,
    products: state.products.products.filter(p => !p.archived),
    productsLoading: state.products.isLoading,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    logout: bindActionCreators(LoginActions.actionCreators.logout, dispatch),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(StockProductManualRestock);
